import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SEARCH } from "@/constants/testIds";
import { CITIES_BY_COUNTRY, PROPERTY_TYPES } from "@/lib/api";
import { Search, RotateCcw } from "lucide-react";

const ANY = "__any__";

export const FilterBar = ({ initial = {}, onSearch, compact = false }) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    country: initial.country || "",
    type: initial.type || "",
    property_type: initial.property_type || "",
    city: initial.city || "",
    min_price: initial.min_price || "",
    max_price: initial.max_price || "",
    rooms: initial.rooms || "",
    q: initial.q || "",
  });

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v === ANY ? "" : v }));

  const submit = (e) => {
    e?.preventDefault?.();
    const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null));
    onSearch?.(clean);
  };

  const reset = () => {
    const empty = { country: "", type: "", property_type: "", city: "", min_price: "", max_price: "", rooms: "", q: "" };
    setFilters(empty);
    onSearch?.({});
  };

  const cities = filters.country ? CITIES_BY_COUNTRY[filters.country] : [];

  return (
    <form
      onSubmit={submit}
      data-testid={SEARCH.filterBar}
      className={`bg-white rounded-2xl border border-[#E5E5E5] ${compact ? "p-4" : "p-5 lg:p-6"} shadow-sm`}
    >
      <div className={`grid gap-3 ${compact ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-6"}`}>
        <Select value={filters.country || ANY} onValueChange={(v) => update("country", v)}>
          <SelectTrigger data-testid={SEARCH.countrySelect} className="h-11 rounded-xl">
            <SelectValue placeholder={t("search.placeholder_country")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("search.countries.all_long")}</SelectItem>
            <SelectItem value="MA">{t("search.countries.MA")}</SelectItem>
            <SelectItem value="DZ">{t("search.countries.DZ")}</SelectItem>
            <SelectItem value="TN">{t("search.countries.TN")}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.type || ANY} onValueChange={(v) => update("type", v)}>
          <SelectTrigger data-testid={SEARCH.typeSelect} className="h-11 rounded-xl">
            <SelectValue placeholder={t("search.placeholder_type")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("search.types.all")}</SelectItem>
            <SelectItem value="vente">{t("search.types.vente")}</SelectItem>
            <SelectItem value="location">{t("search.types.location")}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.property_type || ANY} onValueChange={(v) => update("property_type", v)}>
          <SelectTrigger data-testid={SEARCH.propertyTypeSelect} className="h-11 rounded-xl">
            <SelectValue placeholder={t("search.placeholder_property_type")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("search.placeholder_property_type")}</SelectItem>
            {PROPERTY_TYPES.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.city || ANY} onValueChange={(v) => update("city", v)} disabled={!filters.country}>
          <SelectTrigger data-testid={SEARCH.citySelect} className="h-11 rounded-xl">
            <SelectValue placeholder={t("search.placeholder_city")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("search.placeholder_city")}</SelectItem>
            {cities.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          data-testid={SEARCH.minPrice}
          placeholder={t("search.placeholder_min_price")}
          type="number"
          value={filters.min_price}
          onChange={(e) => update("min_price", e.target.value)}
          className="h-11 rounded-xl"
        />
        <Input
          data-testid={SEARCH.maxPrice}
          placeholder={t("search.placeholder_max_price")}
          type="number"
          value={filters.max_price}
          onChange={(e) => update("max_price", e.target.value)}
          className="h-11 rounded-xl"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto_auto] gap-3 mt-3">
        <Input
          data-testid={SEARCH.searchInput}
          placeholder={t("search.placeholder_keyword")}
          value={filters.q}
          onChange={(e) => update("q", e.target.value)}
          className="h-11 rounded-xl"
        />
        <Input
          data-testid={SEARCH.rooms}
          placeholder={t("search.placeholder_rooms")}
          type="number"
          value={filters.rooms}
          onChange={(e) => update("rooms", e.target.value)}
          className="h-11 rounded-xl"
        />
        <Button
          type="button"
          variant="outline"
          onClick={reset}
          data-testid={SEARCH.resetButton}
          className="h-11 rounded-xl"
        >
          <RotateCcw className="w-4 h-4 me-1" strokeWidth={1.5} /> {t("search.button_reset")}
        </Button>
        <Button
          type="submit"
          data-testid={SEARCH.submitButton}
          className="h-11 rounded-xl bg-[#D6202C] hover:bg-[#B5181F] px-6"
        >
          <Search className="w-4 h-4 me-1" /> {t("search.button_search")}
        </Button>
      </div>
    </form>
  );
};
